import { SquareLock02Icon } from '@hugeicons/react'
import clsx from 'clsx'

import { Badge } from '@/shared/ui/Badge'
import { ProgressBar } from '@/shared/ui/ProgressBar'
import { RewardBadge } from '@/shared/ui/RewardBadge'
import type { StageWithUnderscores } from '@/types/journey'

import { PHASE_STATUS_LABELS, type LevelUiStatus } from '../constants/status'
import { progressVariantForUiStatus } from '../lib/uiStatusTheme'

/* Borda 2px em tinta suave (mix ~32% com branco), não cor de status saturada:
   o card de fase deve ler como o card de nível vizinho — presença sutil,
   status pela tinta, não um anel gritando. */
const PHASE_CARD_CLASS: Record<LevelUiStatus, string> = {
  success:
    'bg-[color-mix(in_srgb,#16a34a_6%,white)] border-[color-mix(in_srgb,#16a34a_32%,white)]',
  progress:
    'bg-[color-mix(in_srgb,#ff9800_8%,white)] border-[color-mix(in_srgb,#ff9800_38%,white)] shadow-[0_1px_4px_rgba(0,0,0,0.05),0_0_0_2px_rgba(255,152,0,0.14)]',
  available:
    'bg-[color-mix(in_oklab,var(--brand)_6%,white)] border-[color-mix(in_oklab,var(--brand)_32%,white)]',
  locked: 'bg-gray-100 border-[#d8dae3]'
}

const PHASE_LABEL_CLASS: Record<LevelUiStatus, string> = {
  success: 'text-status-success',
  progress: 'text-status-progress',
  available: 'text-brand',
  locked: 'text-gray-700'
}

type PhaseCardProps = {
  stage: StageWithUnderscores
  stageIndex: number
  previousStageIndex?: number
  uiStatus: LevelUiStatus
}

export function PhaseCard({
  stage,
  stageIndex,
  previousStageIndex,
  uiStatus
}: PhaseCardProps) {
  const percent = Math.round((stage._progress.finishedPercent ?? 0) * 100)
  const progressVariant = progressVariantForUiStatus(uiStatus)

  const lockHint =
    uiStatus === 'locked' && previousStageIndex
      ? `Complete a Fase ${previousStageIndex} para liberar`
      : uiStatus === 'locked'
        ? 'Complete os requisitos para liberar'
        : null

  return (
    <div
      className={clsx(
        'rounded-xl p-3 flex gap-3 items-start border-2 shadow-[0_1px_4px_rgba(0,0,0,0.05)]',
        PHASE_CARD_CLASS[uiStatus]
      )}
    >
      {stage.icon?.file?.url && (
        <img
          src={stage.icon.file.url}
          alt=""
          className={clsx(
            'w-[72px] h-[72px] rounded-[0.625rem] object-cover shrink-0 block self-center',
            uiStatus === 'locked' && 'grayscale opacity-65'
          )}
        />
      )}

      <div className="flex-1 min-w-0 flex flex-col gap-[0.375rem]">
        <div className="flex gap-2 items-start">
          <div className="flex-1 min-w-0 flex flex-col gap-[0.2rem]">
            <span
              className={clsx(
                'text-caps font-bold uppercase tracking-[0.06em] leading-none',
                PHASE_LABEL_CLASS[uiStatus]
              )}
            >
              FASE {stageIndex}
            </span>
            <span className="text-base font-extrabold text-[#1e2139] leading-[1.25]">
              {stage.name}
            </span>
            <div className="text-2xs text-gray-700 flex items-center gap-1 flex-wrap">
              <span>
                {stage._levels.length}{' '}
                {stage._levels.length === 1 ? 'nível' : 'níveis'}
              </span>
              {stage._totalPointReward > 0 && (
                <>
                  <span className="text-[#c5c7d4] leading-none">•</span>
                  <RewardBadge
                    type="xp"
                    value={
                      uiStatus === 'success'
                        ? (stage._progress.rewards?.acquiredPoints ?? stage._totalPointReward)
                        : stage._totalPointReward
                    }
                    variant="inline"
                  />
                </>
              )}
              {stage._totalCoinReward > 0 && (
                <>
                  <span className="text-[#c5c7d4] leading-none">•</span>
                  <RewardBadge
                    type="coins"
                    value={
                      uiStatus === 'success'
                        ? (stage._progress.rewards?.acquiredCoins ?? stage._totalCoinReward)
                        : stage._totalCoinReward
                    }
                    variant="inline"
                  />
                </>
              )}
            </div>
          </div>

          <div className="shrink-0 flex flex-col items-end gap-[0.375rem]">
            {/* `available` e `progress` sem badge — a tinta do card, a barra e
                o % já comunicam; tag só pra estado terminal ou travado. */}
            {(uiStatus === 'success' || uiStatus === 'locked') && (
              <Badge variant={uiStatus} size="sm" showIcon={false}>
                {PHASE_STATUS_LABELS[uiStatus]}
              </Badge>
            )}
            {uiStatus !== 'locked' ? (
              <span className="text-sm font-bold text-gray-800">{percent}%</span>
            ) : lockHint ? (
              <span className="flex items-start gap-1 text-2xs text-gray-700 leading-[1.35] text-right max-w-[110px]">
                <SquareLock02Icon size={12} />
                {lockHint}
              </span>
            ) : null}
          </div>
        </div>

        {uiStatus !== 'locked' && (
          <div className="mt-1">
            <ProgressBar value={percent} variant={progressVariant} size="sm" />
          </div>
        )}
      </div>
    </div>
  )
}
